import { Button, Loading } from '@affine/component';
import { Pagination, SettingRow } from '@affine/component/setting-components';
import { WorkspaceInvoicesService } from '@affine/core/modules/cloud';
import type { Invoice } from '@affine/core/modules/cloud/entities/workspace-invoices';
import { UrlService } from '@affine/core/modules/url';
import { formatPrice } from '@affine/core/utils/format-price';
import { UserFriendlyError } from '@affine/error';
import { InvoiceStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { cssVar } from '@streamlain/theme';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import * as styles from './styles.css';

export const BillingHistory = () => {
  const t = useI18n();

  const invoicesService = useService(WorkspaceInvoicesService);
  const pageInvoices = useLiveData(invoicesService.invoices.pageInvoices$);
  const invoiceCount = useLiveData(invoicesService.invoices.invoiceCount$);
  const isLoading = useLiveData(invoicesService.invoices.isLoading$);
  const error = useLiveData(invoicesService.invoices.error$);
  const pageNum = useLiveData(invoicesService.invoices.pageNum$);

  useEffect(() => {
    invoicesService.invoices.revalidate();
  }, [invoicesService]);

  const handlePageChange = useCallback(
    (_: number, pageNum: number) => {
      invoicesService.invoices.setPageNum(pageNum);
      invoicesService.invoices.revalidate();
    },
    [invoicesService]
  );

  if (invoiceCount === undefined) {
    if (isLoading) {
      return <BillingHistorySkeleton />;
    } else {
      return (
        <span style={{ color: cssVar('errorColor') }}>
          {error
            ? UserFriendlyError.fromAny(error).message
            : 'Failed to load invoices'}
        </span>
      );
    }
  }

  return (
    <div className={styles.history}>
      <div className={styles.historyContent}>
        {invoiceCount === 0 ? (
          <p className={styles.noInvoice}>
            {t['com.streamlain.payment.billing-setting.no-invoice']()}
          </p>
        ) : (
          pageInvoices?.map(invoice => (
            <InvoiceLine key={invoice.id} invoice={invoice} />
          ))
        )}
      </div>

      {invoiceCount > invoicesService.invoices.PAGE_SIZE && (
        <Pagination
          totalCount={invoiceCount}
          countPerPage={invoicesService.invoices.PAGE_SIZE}
          pageNum={pageNum}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

const InvoiceLine = ({ invoice }: { invoice: Invoice }) => {
  const t = useI18n();
  const urlService = useService(UrlService);

  const open = useCallback(() => {
    if (invoice.link) {
      urlService.openPopupWindow(invoice.link);
    }
  }, [invoice.link, urlService]);

  const openPdf = useCallback(() => {
    if (invoice.pdfUrl) {
      urlService.openPopupWindow(invoice.pdfUrl);
    }
  }, [invoice.pdfUrl, urlService]);

  return (
    <SettingRow
      key={invoice.id}
      name={new Date(invoice.createdAt).toLocaleDateString()}
      desc={`${
        invoice.status === InvoiceStatus.Paid
          ? t['com.streamlain.payment.billing-setting.paid']()
          : ''
      } ${formatPrice(invoice.amount, invoice.currency ?? 'eur')}`}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        {invoice.pdfUrl && (
          <Button onClick={openPdf}>
            {t['com.streamlain.payment.billing-setting.download-pdf']()}
          </Button>
        )}
        <Button onClick={open}>
          {t['com.streamlain.payment.billing-setting.view-invoice']()}
        </Button>
      </div>
    </SettingRow>
  );
};

const BillingHistorySkeleton = () => {
  return (
    <div className={styles.billingHistorySkeleton}>
      <Loading />
    </div>
  );
};
